import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import PencilIcon from '@components/icons/Pencil.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getTranscriptUnlockTypeName } from '@helpers/getTranscriptUnlockTypeName';
import { pluralize } from '@helpers/pluralize';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FindOneSubscriptionForAdminLicenseResponseDto } from 'app/API';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAsOnePlansQueryFn } from 'app/queries/billing/createFetchAsOnePlansQueryFn';
import { subscriptionsKeys } from 'app/queries/subscriptions/subscriptionsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { EntityIcon } from '@components/core-ui/EntityIcon/';
import { createFetchSubscriptionDetailsQueryFn } from './api/fetchSubscriptionDetails';
import { AvatarBlock } from './styles/AvatarBlock';
import { CellContent } from './styles/CellContent';
import { CellContentFlex } from './styles/CellContentFlex';
import { MainContent } from './styles/MainContent';

export const AdminASOneSubscriptionDetails = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal } = useModalContext();
  const { aOneId } = useParams<{ aOneId: string }>();
  const subscriptionId = aOneId ?? '';
  const [search, setSearch] = useState('');

  const plansQuery = useQuery({
    queryKey: billingKeys.allAsOnePlans(),
    queryFn: createFetchAsOnePlansQueryFn(call),
  });
  const subscriptionQuery = useQuery({
    queryKey: subscriptionsKeys.detail(subscriptionId),
    queryFn: createFetchSubscriptionDetailsQueryFn(call),
    enabled: subscriptionId !== '',
  });

  const plans = plansQuery.data ?? [];
  const subscription = subscriptionQuery.data ?? null;
  const isLoading = plansQuery.isFetching || subscriptionQuery.isFetching;

  const unassignedCount = subscription?.Licenses.filter(
    (lic: FindOneSubscriptionForAdminLicenseResponseDto) => lic.assigneeId === null,
  ).length;
  const assignedCount = subscription?.Licenses.filter(
    (lic: FindOneSubscriptionForAdminLicenseResponseDto) => lic.assigneeId !== null,
  ).length;

  const refreshSubscription = () => {
    void queryClient.invalidateQueries({ queryKey: subscriptionsKeys.detail(subscriptionId) });
  };

  const editTranscriptUnlockType = (licenseId: string, initialValue: string) => {
    openModal(ModalNamesEnum.AdminEditLicenseTranscriptUnlockType, {
      baId: subscription?.billingAccountId ?? '',
      subscriptionId: aOneId,
      licenseId,
      initialValue,
      onSuccess: refreshSubscription,
    });
  };

  const editAllTranscriptUnlockTypes = () => {
    openModal(ModalNamesEnum.EditSubscriptionTranscriptUnlockType, {
      baId: subscription?.billingAccountId ?? '',
      subscriptionId: aOneId,
      initialValue: 'SESSION_TEXT',
      onSuccess: refreshSubscription,
    });
  };

  const header = (
    <>
      <Helmet>
        <title>AutoScriptOne Subscription Details - AutoScript</title>
      </Helmet>
      <Header
        title={'Subscription Details'}
        setSearch={setSearch}
        search={search}
        backRoute={routes.adminBillingAccountDetails.make(subscription?.billingAccountId ?? '')}
      />

      <LinearProgressLoader active={isLoading} />
    </>
  );

  const plan = plans.find((plan) => plan.id === subscription?.productId);

  if (!subscription || !plan) {
    return header;
  }

  const renderAvatar = (member: { name: string; lastname: string; avatar?: string | null }) => {
    if (!member) {
      return '';
    }
    return (
      <AvatarBlock className="flex-shrink-0">
        <EntityIcon
          className="flex-shrink-0"
          name={`${member.name} ${member.lastname}`}
          src={member.avatar ?? undefined}
        />
      </AvatarBlock>
    );
  };

  return (
    <>
      {header}
      <MainContent>
        <div className="details">
          <div className="left">
            <div className="name">{plan.name}</div>
            <div className="price">${Number(plan.price).toFixed(2)} Per License Monthly</div>
          </div>
          <div className="divider"></div>
          <div className="count">
            You have{' '}
            <span className="bold">
              {unassignedCount} out of {(assignedCount ?? 0) + (unassignedCount ?? 0)}
            </span>{' '}
            {pluralize((assignedCount ?? 0) + (unassignedCount ?? 0), 'license', 'licenses')} available to assign
          </div>
        </div>

        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Name</Th>
                <Th style={{ width: '50%' }}>Contacts</Th>
                <Th style={{ width: '50%' }}>
                  <div className="flex gap-2 align-middle">
                    Transcript Unlock Type
                    {subscription.allowedToChooseTranscriptUnlockType ? (
                      <PencilIcon
                        className="pencil text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                        onClick={() => editAllTranscriptUnlockTypes()}
                      />
                    ) : null}
                  </div>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {(subscription.Licenses ?? []).map((data: FindOneSubscriptionForAdminLicenseResponseDto) => (
                <Tr key={data.id}>
                  <Td>
                    <CellContent>{data.id}</CellContent>
                  </Td>
                  <Td>
                    {!data.Assignee?.User ? (
                      <CellContent>Unassigned</CellContent>
                    ) : (
                      <CellContentFlex>
                        {renderAvatar(data.Assignee.User)}
                        {data.Assignee.User.name} {data.Assignee.User.lastname}
                      </CellContentFlex>
                    )}
                  </Td>
                  <Td>
                    {!data.Assignee?.User ? (
                      <CellContent></CellContent>
                    ) : (
                      <CellContent>
                        <div>{data.Assignee.User.email}</div>
                        <div>{data.Assignee.User.phone}</div>
                      </CellContent>
                    )}
                  </Td>
                  <Td>
                    <CellContent>
                      <div className="flex gap-1 align-middle">
                        {getTranscriptUnlockTypeName(data.transcriptUnlockType)}
                        {subscription.allowedToChooseTranscriptUnlockType && data.assigneeId ? (
                          <PencilIcon
                            className="pencil text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                            onClick={() => editTranscriptUnlockType(data.id, data.transcriptUnlockType ?? '')}
                          />
                        ) : null}
                      </div>
                    </CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      </MainContent>
    </>
  );
};
